import type {
  MFAccessCatalog,
  MFCatalogItem,
  MFSelectableResourceAccess,
} from "./catalogs";

export type CatalogAccess =
  | { status: "loading"; catalog?: undefined }
  | { status: "no-access"; catalog?: undefined }
  | { status: "ok"; catalog: MFAccessCatalog };

// Resolve a target catalog id against the member's *visible* catalogs. A found
// catalog wins even mid-refetch; only when it's genuinely absent do we report
// `loading` (still fetching) vs `no-access` (loaded, but hidden from this member
// by the catalog's visibility filters — e.g. an admin who deep-linked in). That
// ordering stops the page flashing "no access" before the data arrives.
// "Do I already hold this?" helpers for the catalog surfaces. Matching is by
// underlying resourceId — the one key every current-access row carries — so
// access granted through a sibling selectable resource of the same resource
// still counts.
export const activeAccessResourceIds = (
  accessList: MFSelectableResourceAccess[],
): Set<string> =>
  new Set(
    accessList.filter((a) => a.status === "active").map((a) => a.resourceId),
  );

export const itemHasActiveAccess = (
  item: MFCatalogItem,
  resourceIds: Set<string>,
): boolean =>
  item.catalogItemSelectableResources.some((r) =>
    resourceIds.has(r.selectableResource?.resourceId),
  );

export const resolveCatalogAccess = (
  catalogs: MFAccessCatalog[],
  id: string | undefined,
  isLoading: boolean,
): CatalogAccess => {
  const catalog = id ? catalogs.find((c) => c.id === id) : undefined;
  if (catalog) {
    return { status: "ok", catalog };
  }
  if (isLoading) {
    return { status: "loading" };
  }
  return { status: "no-access" };
};
